import React, { forwardRef, createElement } from 'react'
import classNames from 'classnames'
import './Grid.sass' // Assuming CSS variables and grid classes are defined here
import { generateMarginClasses, generatePaddingClasses } from '@utils/spacing'

const baseClass = 'wb-grid'

/**
 * A Grid is a CSS Grid container that allows you to define grid areas using
 * react components by specifying their grid area names or grid layouts flexibly.
 * It accepts numeric values for columns and gap sizes, and supports responsive layouts.
 */

type GridProps = {
  /** Allows overriding the root HTML tag. */
  as?: keyof JSX.IntrinsicElements
  /** Additional class names to apply to the grid container. */
  className?: string
  /** Number of columns or custom layout string. */
  cols?: number | string
  /** Gap size between grid items. */
  gap?: string | number
  /** Margin classes or styles. */
  margin?: string | object, // Deprecated
  /** Padding classes or styles. */
  padding?: string | object, // Deprecated
  /** Justify content property for the grid. */
  justifyContent?: 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly',
  /** Children elements to be rendered inside the grid. */
  children?: React.ReactNode
}

const Grid = forwardRef<HTMLElement, GridProps>(
  ({ className, as = 'div', cols, gap, children, margin, padding, justifyContent, ...passThroughProps }, ref) => {
    // Deprecated
    const marginClasses = generateMarginClasses(margin)
    const paddingClasses = generatePaddingClasses(padding)

    // Generate grid classes
    const gridColumnsClass = getGridColumnsClass(cols)
    const gapClass = getGapClass(gap)
    const justifyContentClass = justifyContent ? `justify-content-${justifyContent}` : ''

    const classes = classNames(
      baseClass,
      className,
      gapClass,
      gridColumnsClass,
      justifyContentClass,
      paddingClasses,
      marginClasses
    )

    const props = { className: classes, ref, ...passThroughProps }

    // Do not pass layout and gap props directly to DOM
    return createElement(as, props, children)
  }
)

const getGridColumnsClass = (colsProp: GridProps['cols']) => {
  if (colsProp) { return `wb-grid-cols-${colsProp}` }

  return ''
}

// Helper function to generate gap classes
const getGapClass = (gapProp: GridProps['gap']) => {
  if (gapProp) { return `wb-gap-${gapProp}` }

  return ''
}

Grid.displayName = 'Grid'
export { Grid }
export type { GridProps }
